import identity from 'lodash/identity';
import moment from 'moment';
import { keyBy, merge, values } from 'lodash';

/**
 * Converts a value to an array with it being
 * the only item, if the value isn't already an array.
 *
 * @param  {*} value
 * @return {*[]}
 */
export var toArray = function toArray(value) {
  return Array.isArray(value) ? value : [value];
};

/**
 * Makes a moment sorter function to be passed into an array.
 *
 * @param  {String}   direction
 * asc or desc
 *
 * @param  {Function} getter
 * Invoked to get the moment on the element. Default is identity.
 * '
 * @param  {'first' | 'last' | undefined} nulls
 * Whether nulls go first or last. If undefined, nulls go first when asc, last when desc.
 *
 * @return {Function}
 * A function to pass into Array.prototype.sort
 */
export var makeMomentSorter = function makeMomentSorter() {
  var direction = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'asc';
  var getter = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : identity;
  var nulls = arguments.length > 2 ? arguments[2] : undefined;
  return function (left, right) {
    var leftComesFirst = direction === 'asc' ? -1 : 1;
    var rightComesFirst = direction === 'asc' ? 1 : -1;
    if (!nulls) {
      nulls = direction === 'asc' ? 'first' : 'last';
    }
    if (!left && !right) {
      return 0;
    }
    if (!left && right) {
      switch (nulls) {
        case 'first':
          return -1;
        case 'last':
          return 1;
      }
    }
    if (left && !right) {
      switch (nulls) {
        case 'first':
          return 1;
        case 'last':
          return -1;
      }
    }
    var leftMoment = moment(getter(left));
    var rightMoment = moment(getter(right));
    if (leftMoment.isBefore(rightMoment)) return leftComesFirst;
    if (leftMoment.isAfter(rightMoment)) return rightComesFirst;
    return 0;
  };
};

/**
 * Like groupBy, but groups by whether the iteratee returns true or false.
 *
 * @param array
 * @param iteratee
 * @return
 * An array where the first item is the true array, and the second is the false array.
 * @example
 *    const [even, odd] = groupByBinary([1, 2, 3, 4], i => i % 2 === 0)
 *    even = [2, 4]
 *    odd = [1, 3]
 */
export var groupByBinary = function groupByBinary(array, iteratee) {
  var trueItems = [];
  var falseItems = [];
  array.forEach(function (item) {
    if (iteratee(item)) {
      trueItems.push(item);
    } else {
      falseItems.push(item);
    }
  });
  return [trueItems, falseItems];
};

/**
 * Maps items and adds separators between them.
 *
 * @typedef {Object} T
 * @param {T[]} source
 * @param {*} makeSeparator
 * @returns {T[]}
 */
export function addSeparators(source, makeSeparator) {
  var result = [];
  var previousItem;
  var resultingIndex = 0;
  for (var itemIndex = 0; itemIndex < source.length; itemIndex++) {
    var item = source[itemIndex];
    if (itemIndex === 0) {
      previousItem = item;
      result.push(item);
      continue;
    }
    var separator = makeSeparator(previousItem, item, ++resultingIndex, source.length + Math.max(source.length - 2, 0));
    resultingIndex++;
    previousItem = item;
    result.push(separator);
    result.push(item);
  }
  return result;
}

/**
 * Outer join two arrays:
 * ```sourceLeft outer join sourceRight on sourceLeft.keyLeft = sourceRight.keyRight```
 * @param {*[]} sourceLeft the first array to join
 * @param {string} keyLeft the outer join key of the first array
 * @param {*[]} sourceRight the second array to join
 * @param {string} keyRight the outer join key of the second array
 * @returns {*[]}
 */
export function outerJoin(sourceLeft, keyLeft, sourceRight, keyRight) {
  var merged = merge(keyBy(sourceLeft, keyLeft), keyBy(sourceRight, keyRight));
  var result = values(merged);
  return result;
}
//# sourceMappingURL=arrayUtil.js.map
